import { AlertTriangle, X, Eye, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatLeadPhone } from '@/components/ui/PhoneInput';

interface DuplicateLead {
  id: string;
  name: string;
  // Backend now returns LeadPhone[] / LeadEmail[] (objects), not string arrays.
  phones: { isdCode?: string | null; phoneNumber?: string | null; isPrimary?: boolean }[];
  emails: { email: string; isPrimary?: boolean }[];
  status: string;
}

interface Props {
  duplicates: DuplicateLead[];
  onCreateAnyway: () => void;
  onClose: () => void;
}

export function DuplicateWarningModal({ duplicates, onCreateAnyway, onClose }: Props) {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>

        <div className="flex flex-col items-center pt-8 pb-4 px-6">
          <div className="h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-amber-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Possible Duplicates Found</h2>
          <p className="text-sm text-slate-500 mt-1 text-center">
            {duplicates.length} existing lead{duplicates.length > 1 ? 's' : ''} match the phone or email you entered.
          </p>
        </div>

        <div className="px-6 max-h-[200px] overflow-y-auto">
          {duplicates.map(d => (
            <div key={d.id} className="flex items-center justify-between py-2.5 border-b border-gray-50">
              <div>
                <p className="text-sm font-medium text-slate-800">{d.name}</p>
                <p className="text-xs text-slate-400">
                  {formatLeadPhone(d.phones?.[0]) || (d.emails?.[0]?.email) || '—'} · {d.status}
                </p>
              </div>
              <button
                onClick={() => { onClose(); navigate(`/leads/${d.id}`); }}
                className="text-xs text-[#6366F1] hover:underline flex items-center gap-1"
              >
                <Eye className="h-3 w-3" /> View
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-3 px-6 py-5">
          <button onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-gray-200 text-sm font-medium text-slate-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={onCreateAnyway}
            className="flex-1 h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold flex items-center justify-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Create Anyway
          </button>
        </div>
      </div>
    </div>
  );
}
